/**
 * Generador pseudoaleatorio determinista y serializable.
 *
 * `Math.random()` no sirve aquí: en la Fase 2 el servidor tiene que poder
 * re-simular una partida a partir de la semilla y del registro de jugadas para
 * detectar trampas. Por eso el azar es una función pura de (semilla, contador):
 * guardar esos dos enteros en el estado basta para reproducir la partida entera,
 * y no hay ningún objeto mutable escondido.
 */

/** Posición dentro de la secuencia. Vive dentro de GameState. */
export interface RngState {
  seed: number;
  cursor: number;
}

/**
 * Mezcla dos enteros de 32 bits en uno bien distribuido (variante de murmur3).
 * Se llama con (semilla, contador), así que valores de cursor consecutivos dan
 * resultados sin correlación aparente.
 */
function mix32(seed: number, cursor: number): number {
  let h = (seed ^ Math.imul(cursor + 0x9e3779b9, 0x85ebca6b)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2545f491) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h, 0xc2b2ae35) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/** Float en [0, 1) y el cursor ya avanzado. */
export function nextFloat(state: RngState): [value: number, next: RngState] {
  const raw = mix32(state.seed, state.cursor);
  return [raw / 0x100000000, { seed: state.seed, cursor: state.cursor + 1 }];
}

/** Entero en [0, maxExclusive). Devuelve 0 si el rango es vacío. */
export function nextInt(state: RngState, maxExclusive: number): [value: number, next: RngState] {
  if (maxExclusive <= 0) return [0, state];
  const [value, next] = nextFloat(state);
  return [Math.floor(value * maxExclusive), next];
}

/** Elemento al azar. Devuelve undefined solo si el array está vacío. */
export function pick<T>(state: RngState, items: readonly T[]): [value: T | undefined, next: RngState] {
  const [index, next] = nextInt(state, items.length);
  return [items[index], next];
}

/**
 * Copia barajada con Fisher-Yates. No toca el array original: el motor trata
 * todo el estado como inmutable.
 */
export function shuffle<T>(state: RngState, items: readonly T[]): [value: T[], next: RngState] {
  const result = [...items];
  let cursor = state;
  for (let i = result.length - 1; i > 0; i -= 1) {
    const [j, next] = nextInt(cursor, i + 1);
    cursor = next;
    const a = result[i];
    const b = result[j];
    // La guarda es para el comprobador de tipos (noUncheckedIndexedAccess);
    // por construcción i y j siempre están dentro del array.
    if (a !== undefined && b !== undefined) {
      result[i] = b;
      result[j] = a;
    }
  }
  return [result, cursor];
}

/** Semilla nueva para una partida sin semilla explícita. */
export function randomSeed(): number {
  return Math.floor(Math.random() * 0xffffffff) >>> 0;
}
